"use client";
import { useEffect, useState, useRef, useCallback } from "react";

/**
 * Configuration options for the useMarquee hook
 */
interface UseMarqueeOptions {
    /** Speed of the marquee animation in pixels per second (default: 30) */
    speed?: number;
    /** Whether to pause the animation on hover (default: true) */
    pauseOnHover?: boolean;
    /** Gap between the end of text and start of duplicate in pixels (default: 50) */
    gap?: number;
    /** Minimum overflow in pixels required to trigger scrolling (default: 5) */
    threshold?: number;
    /** Whether the marquee is currently enabled (default: true) */
    enabled?: boolean;
}

/**
 * Return type for the useMarquee hook
 */
interface UseMarqueeReturn {
    /** Ref to attach to the container element */
    containerRef: React.RefObject<HTMLDivElement | null>;
    /** Ref to attach to the content element */
    contentRef: React.RefObject<HTMLDivElement | null>;
    /** Whether the content should scroll (true when content overflows) */
    shouldScroll: boolean;
    /** Props to apply to the marquee wrapper for animation */
    marqueeProps: {
        style: React.CSSProperties;
        onMouseEnter?: () => void;
        onMouseLeave?: () => void;
    };
}

/**
 * useMarquee - A performant custom hook for creating horizontal scrolling text animations
 *
 * This hook provides automatic overflow detection and smooth CSS-based scrolling animations
 * for text that exceeds its container width. It uses GPU-accelerated CSS transforms for
 * optimal performance and includes features like pause on hover and configurable speed.
 *
 * @example
 * ```tsx
 * const MyComponent = () => {
 *   const { containerRef, contentRef, marqueeProps } = useMarquee({
 *     speed: 30, // 30 pixels per second
 *     pauseOnHover: true,
 *     gap: 50 // 50px gap between text repeats
 *   });
 *
 *   return (
 *     <div ref={containerRef} style={{ overflow: 'hidden', width: '200px' }}>
 *       <div {...marqueeProps}>
 *         <span ref={contentRef}>This is a very long text that will scroll</span>
 *         <span>This is a very long text that will scroll</span>
 *       </div>
 *     </div>
 *   );
 * };
 * ```
 *
 * @param {UseMarqueeOptions} options - Configuration options for the marquee behavior
 * @returns {UseMarqueeReturn} Refs and props needed to implement the marquee effect
 */
export const useMarquee = (options: UseMarqueeOptions = {}): UseMarqueeReturn => {
    const { speed = 30, pauseOnHover = true, gap = 50, threshold = 5, enabled = true } = options;

    const [animationDuration, setAnimationDuration] = useState(10);
    const [isPaused, setIsPaused] = useState(false);
    const [shouldScroll, setShouldScroll] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const rafId = useRef<number | null>(null);

    /**
     * Checks if the content overflows its container and calculates animation duration
     */
    const checkOverflow = useCallback(() => {
        if (rafId.current !== null) {
            cancelAnimationFrame(rafId.current);
        }

        rafId.current = requestAnimationFrame(() => {
            if (containerRef.current && contentRef.current) {
                const containerWidth = containerRef.current.clientWidth;
                const contentWidth = contentRef.current.scrollWidth;
                const overflow = contentWidth - containerWidth;

                // Only trigger scrolling if overflow exceeds threshold
                const shouldActivateScroll = enabled && overflow > threshold;
                setShouldScroll(shouldActivateScroll);

                if (shouldActivateScroll) {
                    // Calculate animation duration based on content width and speed
                    // For 50% translation with duplicated content
                    const totalDistance = contentWidth + gap;
                    const duration = totalDistance / speed;
                    setAnimationDuration(duration);
                }
            }
        });
    }, [speed, gap, threshold, enabled]);

    /**
     * Set up ResizeObserver for responsive overflow detection
     */
    useEffect(() => {
        checkOverflow();

        let resizeObserver: ResizeObserver | null = null;

        if (typeof ResizeObserver !== "undefined") {
            resizeObserver = new ResizeObserver(() => {
                checkOverflow();
            });

            if (containerRef.current) {
                resizeObserver.observe(containerRef.current);
            }
            if (contentRef.current) {
                resizeObserver.observe(contentRef.current);
            }
        } else {
            // Fallback to window resize event for older browsers
            window.addEventListener("resize", checkOverflow);
        }

        return () => {
            if (rafId.current !== null) {
                cancelAnimationFrame(rafId.current);
            }
            if (resizeObserver) {
                resizeObserver.disconnect();
            } else {
                window.removeEventListener("resize", checkOverflow);
            }
        };
    }, [checkOverflow]);

    /**
     * Handle pause on hover
     */
    const handleMouseEnter = useCallback(() => {
        if (pauseOnHover) {
            setIsPaused(true);
        }
    }, [pauseOnHover]);

    const handleMouseLeave = useCallback(() => {
        if (pauseOnHover) {
            setIsPaused(false);
        }
    }, [pauseOnHover]);

    const marqueeStyle: React.CSSProperties = {
        display: "flex",
        animation: `marquee ${animationDuration}s linear infinite`,
        animationPlayState: isPaused ? "paused" : "running",
        willChange: "transform",
    };

    return {
        containerRef,
        contentRef,
        shouldScroll,
        marqueeProps: {
            style: marqueeStyle,
            ...(pauseOnHover && {
                onMouseEnter: handleMouseEnter,
                onMouseLeave: handleMouseLeave,
            }),
        },
    };
};
